import { ReactElement, useCallback, useMemo } from 'react';
import {
  Stack,
  Typography,
  Divider,
  FormControl,
  FormHelperText,
  Switch,
  MenuItem,
} from '@mui/material';
import PreferencesIcon from '@mui/icons-material/Tune';
import { LoadingButton } from '@mui/lab';
import { useFormik, FormikProvider, FormikHelpers } from 'formik';
import { has } from 'lodash';

import {
  PlanFeaturesTypes,
  PreferencesTrackingComplaintChannelTypeId,
} from '~globals/types/enums';
import { Nullable } from '~globals/types/commons';
import {
  AdminCompanySettingUpdate,
  AdminCompanySetting,
} from '~services/admin/companySetting/types';
import { setUserCompanyType } from '~store/slices/auth';
import { useAppDispatch } from '~hooks/useAppDispatch';
import { useCompanySettingsAdminUpdate } from '~hooks/useCompanySettingAdminUpdate';
import { trackingComplainTypes } from '~constants/trackingComplaintTypes';
import { hasError } from '~utils/formHelpers';
import { getFeatureAvailableToDate } from '~utils/feature';
import {
  validateComplaintType,
  getComplaintLinkHrefEmail,
  getComplainHrefWhatsapp,
} from '~modules/Config/Preferences/form/complaint';
import AccessFeature from '~components/AccessFeature';

import { DialogConfigAndPreferencesSectionCard } from '../styles';

import { DialogConfigAndPreferencesSectionPreferencesProps } from './types';
import { FIELDS_NAME, getInitialValues, validationSchema } from './utils';

const DialogConfigAndPreferencesSectionPreferences = ({
  data,
  reload,
}: DialogConfigAndPreferencesSectionPreferencesProps): ReactElement => {
  const dispatch = useAppDispatch();

  const handleSuccessUpdate = useCallback(
    (settings: AdminCompanySetting) => {
      dispatch(setUserCompanyType(settings.companyTypeId));
      reload();
    },
    [dispatch, reload],
  );

  const { loading, handleUpdateCompanySetting } =
    useCompanySettingsAdminUpdate(handleSuccessUpdate);

  const executeSubmit = useCallback(
    async (
      values: ReturnType<typeof getInitialValues>,
      { setSubmitting }: FormikHelpers<ReturnType<typeof getInitialValues>>,
    ) => {
      const complaintChannelTypeId = Number(
        values[FIELDS_NAME.COMPLAINT_CHANNEL_TYPE],
      );

      let complaintAddress: Nullable<string> = null;

      if (
        validateComplaintType(
          complaintChannelTypeId,
          PreferencesTrackingComplaintChannelTypeId.Email,
        )
      ) {
        complaintAddress = values[FIELDS_NAME.COMPLAINT_EMAIL];
      }

      if (
        validateComplaintType(
          complaintChannelTypeId,
          PreferencesTrackingComplaintChannelTypeId.Whatsapp,
        )
      ) {
        complaintAddress = values[FIELDS_NAME.COMPLAINT_PHONE];
      }

      const body: AdminCompanySettingUpdate = {
        complaintChannelTypeId,
        complaintAddress,
        includeDieInTicketPdf: values[FIELDS_NAME.INCLUDE_DIE_IN_TICKET_PDF],
        zebraModeInTicketPdf: values[FIELDS_NAME.ZEBRA_MODE_IN_TICKET_PDF],
        oneTicketByArticle: values[FIELDS_NAME.ONE_TICKET_BY_ARTICLE],
      };

      await handleUpdateCompanySetting(body);
      setSubmitting(false);
    },
    [handleUpdateCompanySetting],
  );

  const formikBag = useFormik({
    initialValues: getInitialValues(data),
    validationSchema,
    onSubmit: executeSubmit,
  });

  const {
    errors,
    touched,
    getFieldProps,
    handleSubmit,
    isSubmitting,
    dirty,
    values,
  } = formikBag;

  const complaintChannelVal = useMemo(
    () => Number(values[FIELDS_NAME.COMPLAINT_CHANNEL_TYPE]),
    [values],
  );

  const complaintChannelButtonHref = useMemo(() => {
    let href = '';
    const fakeTrackingCode = 'RO-XXXXXXXX-XXXXXXXX';

    if (
      validateComplaintType(
        complaintChannelVal,
        PreferencesTrackingComplaintChannelTypeId.Email,
      ) &&
      !has(errors, FIELDS_NAME.COMPLAINT_EMAIL)
    ) {
      href = getComplaintLinkHrefEmail(
        values[FIELDS_NAME.COMPLAINT_EMAIL],
        fakeTrackingCode,
      );
    }

    if (
      validateComplaintType(
        complaintChannelVal,
        PreferencesTrackingComplaintChannelTypeId.Whatsapp,
      ) &&
      !has(errors, FIELDS_NAME.COMPLAINT_PHONE)
    ) {
      href = getComplainHrefWhatsapp(
        values[FIELDS_NAME.COMPLAINT_PHONE],
        fakeTrackingCode,
      );
    }

    return href;
  }, [complaintChannelVal, values, errors]);

  return (
    <FormikProvider value={formikBag}>
      <DialogConfigAndPreferencesSectionCard>
        <Stack spacing={2} component="form" onSubmit={handleSubmit}>
          <Stack spacing={1} direction="row" alignItems="center">
            <PreferencesIcon />

            <Typography variant="h6" component="h5" fontWeight="bold">
              Preferencias
            </Typography>
          </Stack>

          <Stack spacing={2} divider={<Divider />}>
            <Stack spacing={2}>
              <Typography
                component="h6"
                fontWeight="bold"
                color="text.secondary"
              >
                Configuraciones generales
              </Typography>

              <Stack>
                <Stack spacing={2} direction="row" alignItems="center">
                  <AccessFeature.TextField
                    validFeature={PlanFeaturesTypes.TrackingPortalModule}
                    showChip={false}
                    label="Canal de reclamos"
                    autoComplete="off"
                    error={hasError(
                      touched,
                      errors,
                      FIELDS_NAME.COMPLAINT_CHANNEL_TYPE,
                    )}
                    {...getFieldProps(FIELDS_NAME.COMPLAINT_CHANNEL_TYPE)}
                    InputLabelProps={{ shrink: true }}
                    select
                    sx={{ minWidth: 200 }}
                  >
                    {trackingComplainTypes.map((complainType) => (
                      <MenuItem
                        key={`tracking-complain-type-${complainType.value}`}
                        value={String(complainType.value)}
                      >
                        {complainType.label}
                      </MenuItem>
                    ))}
                  </AccessFeature.TextField>

                  {validateComplaintType(
                    complaintChannelVal,
                    PreferencesTrackingComplaintChannelTypeId.Email,
                  ) && (
                    <AccessFeature.TextField
                      validFeature={PlanFeaturesTypes.TrackingPortalModule}
                      showChip={false}
                      openAccessFeatureModalOnClickChip={false}
                      label="Email"
                      autoComplete="off"
                      error={hasError(
                        touched,
                        errors,
                        FIELDS_NAME.COMPLAINT_EMAIL,
                      )}
                      {...getFieldProps(FIELDS_NAME.COMPLAINT_EMAIL)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 200 }}
                    />
                  )}

                  {validateComplaintType(
                    complaintChannelVal,
                    PreferencesTrackingComplaintChannelTypeId.Whatsapp,
                  ) && (
                    <AccessFeature.TextField
                      validFeature={PlanFeaturesTypes.TrackingPortalModule}
                      showChip={false}
                      label="Nro. de WhatsApp"
                      autoComplete="off"
                      error={hasError(
                        touched,
                        errors,
                        FIELDS_NAME.COMPLAINT_PHONE,
                      )}
                      {...getFieldProps(FIELDS_NAME.COMPLAINT_PHONE)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 200 }}
                    />
                  )}

                  {!validateComplaintType(
                    complaintChannelVal,
                    PreferencesTrackingComplaintChannelTypeId.None,
                  ) && (
                    <AccessFeature.Button
                      validFeature={PlanFeaturesTypes.TrackingPortalModule}
                      showChip={false}
                      color="primary"
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        window.open(complaintChannelButtonHref, '_blank')
                      }
                      disabled={!complaintChannelButtonHref}
                      sx={{ minWidth: 200 }}
                    >
                      Enviar reclamo de prueba
                    </AccessFeature.Button>
                  )}
                </Stack>

                <FormHelperText>
                  Ofrece a tus clientes la posibilidad de realizar reclamos o
                  reportar inconvenientes con sus pedidos
                </FormHelperText>
              </Stack>
            </Stack>

            <Stack spacing={2}>
              <AccessFeature.Typograghy
                validFeature={PlanFeaturesTypes.MgmGenerateTickets}
                availableToDate={getFeatureAvailableToDate(
                  PlanFeaturesTypes.MgmGenerateTickets,
                )}
                component="h6"
                fontWeight="bold"
                color="text.secondary"
              >
                Etiquetas de pedidos
              </AccessFeature.Typograghy>

              <AccessFeature.Alert
                validFeature={PlanFeaturesTypes.MgmGenerateTickets}
                availableToDate={getFeatureAvailableToDate(
                  PlanFeaturesTypes.MgmGenerateTickets,
                )}
              />

              <FormControl>
                <AccessFeature.FormControlLabel
                  validFeature={PlanFeaturesTypes.MgmGenerateTickets}
                  availableToDate={getFeatureAvailableToDate(
                    PlanFeaturesTypes.MgmGenerateTickets,
                  )}
                  showChip={false}
                  control={
                    <Switch
                      {...getFieldProps(FIELDS_NAME.ZEBRA_MODE_IN_TICKET_PDF)}
                      checked={values[FIELDS_NAME.ZEBRA_MODE_IN_TICKET_PDF]}
                    />
                  }
                  label="Compatibilidad para impresoras térmicas"
                  sx={{ margin: 0 }}
                />

                <FormHelperText>
                  Activando esta opción se exportará una etiqueta por hoja,
                  considerando como medida de hoja 10x15 centímetros. En caso
                  contrario, se exportan hasta 4 etiquetas en tamaño de hoja A4.
                </FormHelperText>
              </FormControl>

              <FormControl>
                <AccessFeature.FormControlLabel
                  validFeature={PlanFeaturesTypes.MgmGenerateTickets}
                  availableToDate={getFeatureAvailableToDate(
                    PlanFeaturesTypes.MgmGenerateTickets,
                  )}
                  showChip={false}
                  control={
                    <Switch
                      {...getFieldProps(FIELDS_NAME.INCLUDE_DIE_IN_TICKET_PDF)}
                      checked={values[FIELDS_NAME.INCLUDE_DIE_IN_TICKET_PDF]}
                    />
                  }
                  label="Incluir troquel de control en la etiqueta"
                  sx={{ margin: 0 }}
                />

                <FormHelperText>
                  El troquel de control permite agilizar la tarea de control al
                  momento de la preparación del pedido. Cuando esta opción se
                  encuentra activa, la etiqueta puede crecer en altura.
                </FormHelperText>
              </FormControl>

              <FormControl>
                <AccessFeature.FormControlLabel
                  validFeature={PlanFeaturesTypes.MgmGenerateTickets}
                  availableToDate={getFeatureAvailableToDate(
                    PlanFeaturesTypes.MgmGenerateTickets,
                  )}
                  showChip={false}
                  control={
                    <Switch
                      {...getFieldProps(FIELDS_NAME.ONE_TICKET_BY_ARTICLE)}
                      checked={values[FIELDS_NAME.ONE_TICKET_BY_ARTICLE]}
                    />
                  }
                  label="Generar una etiqueta por artículo"
                  sx={{ margin: 0 }}
                />

                <FormHelperText>
                  Activa esta opción si deseas que se imprima una etiqueta con
                  troquel personalizado por artículo. Si se desactiva, las
                  etiquetas se imprimiran por bulto.
                </FormHelperText>
              </FormControl>
            </Stack>
          </Stack>

          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            loading={loading || isSubmitting}
            disabled={!dirty}
            sx={{ maxWidth: 200 }}
          >
            Guardar
          </LoadingButton>
        </Stack>
      </DialogConfigAndPreferencesSectionCard>
    </FormikProvider>
  );
};

export default DialogConfigAndPreferencesSectionPreferences;
